import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { accounts as accountQueries, type AccountWithScopeDeactivationDates } from "src/queries/accounts";
import { EMPTY_ARRAY, EMPTY_MAP } from "src/utils/empty-values";
import { parseMaybeDateString } from "src/utils/parse-maybe-date-string";
import type { AccountScopeDeactivationDates } from "../../../activation-status/activation-status-types";

export enum AccountValueField {
  AccountId = "accountId",
  AccountName = "accountName",
}

const accountValueGetters: Record<AccountValueField, (account: AccountWithScopeDeactivationDates) => string> = {
  [AccountValueField.AccountId]: account => account.ID.toString(),
  [AccountValueField.AccountName]: account => account.DISPLAY_NAME ?? account.NAME,
};

export const useAccountOptions = (valueField: AccountValueField = AccountValueField.AccountName) => {
  const { data: accountList } = useQuery({
    ...accountQueries.listWithScopeDeactivationDates,
    staleTime: 30000,
  });

  const accountOptions = useMemo(() => {
    if (!accountList) return EMPTY_ARRAY;
    const getValue = accountValueGetters[valueField];
    return accountList.ACCOUNTS.map(account => ({
      value: getValue(account),
      title: account.DISPLAY_NAME.trim(),
      label: account.DISPLAY_NAME.trim(),
      id: account.ID,
    }));
  }, [accountList, valueField]);

  return accountOptions;
};

export const useAccountScopeDeactivationDatesMap = () => {
  const { data: accountList } = useQuery({
    ...accountQueries.listWithScopeDeactivationDates,
    staleTime: 30000,
  });

  const accounts = useMemo<Map<number, AccountScopeDeactivationDates>>(() => {
    if (!accountList) return EMPTY_MAP;
    const map = new Map<number, AccountScopeDeactivationDates>();
    for (const account of accountList.ACCOUNTS) {
      map.set(account.ID, {
        id: account.ID,
        name: account.NAME,
        displayName: account.DISPLAY_NAME,
        isStoreActive: account.IS_ACTIVE,
        chargebacksDeactivationDate: parseMaybeDateString(account.SCOPE_DEACTIVATION_DATES.CHARGEBACKS),
        shortagesDeactivationDate: parseMaybeDateString(account.SCOPE_DEACTIVATION_DATES.SHORTAGES),
        priceClaimsDeactivationDate: parseMaybeDateString(account.SCOPE_DEACTIVATION_DATES.PRICE_CLAIMS),
        accrualsDeactivationDate: parseMaybeDateString(account.SCOPE_DEACTIVATION_DATES.ACCRUALS),
      });
    }
    return map;
  }, [accountList]);

  return accounts;
};
